import { Field } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import {
  LineForComboboxFragment,
  RouteAllFieldsFragment,
} from '../../../../../generated/graphql';
import {
  selectEditedRouteData,
  selectHasDraftRouteGeometry,
  selectMapRouteEditor,
  setTemplateRouteIdAction,
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux';
import { mapToValidityPeriod } from '../../../../../utils';
import {
  ChangeValidityForm,
  FormActionButtons,
  hasSavableDirtyFields,
} from '../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../../common/Inputs';
import { Switch, SwitchLabel } from '../../../../common/Jore';
import { FormColumn, FormRow, Row } from '../../../../common/LayoutComponents';
import {
  RouteFormState,
  mapRouteToFormState,
  routeFormSchema,
} from '../../../Common';
import { ChooseLineDropdown } from './ChooseLineDropdown';
import { DirectionDropdown } from './DirectionDropdown';
import { TemplateRouteSelector } from './TemplateRouteSelector';
import { TerminusNameInputs } from './TerminusNameInputs';
import { useRouteLineValidityConflict } from './useRouteLineValidityConflict';

export type RoutePropertiesFormProps = {
  readonly id?: string;
  readonly routeLabel?: string | null;
  readonly className?: string;
  readonly defaultValues: Partial<RouteFormState>;
  readonly onSubmit: (state: RouteFormState) => void;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
  readonly onDelete?: () => void;
  readonly deleteButtonText?: string;
  readonly actionButtonsClassName?: string;
  readonly variant?: 'infoContainer' | 'modal';
  readonly modalLayout?: boolean;
};

const testIds = {
  directionDropdown: 'RoutePropertiesFormComponent::directionDropdown',
  lineChoiceDropdown: 'RoutePropertiesFormComponent::chooseLineDropdown',
  label: 'RoutePropertiesFormComponent::label',
  finnishName: 'RoutePropertiesFormComponent::finnishName',
  variant: 'RoutePropertiesFormComponent::variant',
  versionComment: 'RoutePropertiesFormComponent::versionComment',
  lineValidityError: 'RoutePropertiesFormComponent::lineValidityError',
  useTemplateRouteButton:
    'RoutePropertiesFormComponent::useTemplateRouteButton',
};

export const RoutePropertiesFormComponent: ForwardRefRenderFunction<
  HTMLFormElement,
  RoutePropertiesFormProps
> = (
  {
    id,
    routeLabel,
    className,
    defaultValues,
    onSubmit,
    onCancel,
    testIdPrefix,
    onDelete,
    deleteButtonText,
    actionButtonsClassName,
    variant,
    modalLayout,
  },
  ref,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { creatingNewRoute } = useAppSelector(selectMapRouteEditor);
  const { templateRouteId } = useAppSelector(selectEditedRouteData);
  const hasDraftRouteGeometry = useAppSelector(selectHasDraftRouteGeometry);

  const methods = useForm<RouteFormState>({
    defaultValues,
    resolver: zodResolver(routeFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'RoutePropertiesForm', {
    allowSearchChange: true, // Allow search change so that moving the map does not show the navigation blocked dialog
    allowStateChange: true, // Allow state change so that location state updates do not show the navigation blocked dialog
  });

  const [showTemplateRouteSelector, setShowTemplateRouteSelector] =
    useState(false);

  const { handleSubmit } = methods;
  const hasSavableChanges = hasSavableDirtyFields({
    dirtyFields: methods.formState.dirtyFields,
    ignoredFields: ['versionComment'],
  });

  const [validityStart, validityEnd, indefinite] = methods.watch([
    'validityStart',
    'validityEnd',
    'indefinite',
  ]);

  const [selectedLine, setSelectedLine] =
    useState<LineForComboboxFragment | null>(null);
  const handleLineSelected = useCallback(
    (line: LineForComboboxFragment | undefined) =>
      setSelectedLine(line ?? null),
    [],
  );

  const { hasConflict } = useRouteLineValidityConflict({
    line: selectedLine,
    validityStart,
    validityEnd,
    indefinite,
  });

  const setTemplateRoute = (templateRoute?: RouteAllFieldsFragment) => {
    dispatch(setTemplateRouteIdAction(templateRoute?.route_id));

    if (templateRoute) {
      methods.reset(mapRouteToFormState(templateRoute), {
        keepDefaultValues: true,
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id={id ?? 'route-properties-form'}
        className={twMerge('flex flex-col', className)}
        onSubmit={handleSubmit(onSubmit)}
        ref={ref}
      >
        <div className="space-y-6 overflow-auto">
          {routeLabel && (
            <Row>
              <h2 className="mb-8">
                {t(($) => $.routes.route)} {routeLabel}
              </h2>
            </Row>
          )}
          <FormColumn className={twMerge('p-4', className)}>
            {creatingNewRoute && (
              <>
                <Field as={Row} className="flex-auto items-center">
                  <SwitchLabel className="my-1 mr-2">
                    {t(($) => $.routes.useTemplateRoute)}
                  </SwitchLabel>
                  <Switch
                    checked={showTemplateRouteSelector}
                    disabled={hasDraftRouteGeometry}
                    testId={testIds.useTemplateRouteButton}
                    onChange={(enabled: boolean) => {
                      setShowTemplateRouteSelector(enabled);

                      if (!enabled) {
                        setTemplateRoute(undefined);
                      }
                    }}
                  />
                </Field>
                {showTemplateRouteSelector && (
                  <TemplateRouteSelector
                    value={templateRouteId}
                    onChange={setTemplateRoute}
                  />
                )}
              </>
            )}
            <FormRow
              className="sm:gap-x-4 md:gap-x-4 lg:gap-x-4"
              mdColumns={5}
              smColumns={4}
            >
              <InputField<RouteFormState>
                type="text"
                translationPrefix="routes"
                fieldPath="finnishName"
                testId={testIds.finnishName}
                className="sm:col-span-3"
                required
              />
              <InputField<RouteFormState>
                type="text"
                translationPrefix="routes"
                fieldPath="label"
                testId={testIds.label}
                className="col-span-1"
                required
              />
              <InputField<RouteFormState>
                type="number"
                translationPrefix="routes"
                fieldPath="variant"
                testId={testIds.variant}
                className="col-span-1"
                min={0}
              />
              <InputField<RouteFormState>
                translationPrefix="routes"
                fieldPath="direction"
                testId={testIds.directionDropdown}

                inputElementRenderer={(props) => (
                  <DirectionDropdown {...props} />
                )}
                className="col-span-2"
                required
              />
              <InputField<RouteFormState, string | null>
                translationPrefix="routes"
                fieldPath="onLineId"
                testId={testIds.lineChoiceDropdown}

                inputElementRenderer={(props) => (
                  <ChooseLineDropdown
                    {...props}
                    onLineSelected={handleLineSelected}
                  />
                )}
                className="sm:col-span-3"
                required
              />
            </FormRow>
          </FormColumn>
          <TerminusNameInputs />
          <FormRow className="border-t border-light-grey p-4">
            <ChangeValidityForm<RouteFormState>
              dateInputRowClassName="sm:gap-x-4 md:gap-x-4 lg:gap-x-4"
              modalLayout={modalLayout}
              title={t(($) => $.routes.routeValidityPeriod)}
              versionCommentField={{
                translationPrefix: 'routes',
                customTitlePath: 'reasonForChangeForm.reasonForChange',
                testId: testIds.versionComment,
              }}
              validityPeriodError={
                hasConflict && selectedLine ? (
                  <p
                    className="mt-3.5 text-sm font-bold text-hsl-red"
                    data-testid={testIds.lineValidityError}
                  >
                    {t(($) => $.routes.lineValidityNotEnough, {
                      validity: mapToValidityPeriod(
                        t,
                        selectedLine.validity_start,
                        selectedLine.validity_end,
                      ),
                    })}
                  </p>
                ) : null
              }
            />
          </FormRow>
        </div>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !hasSavableChanges || methods.formState.isSubmitting || hasConflict
          }
          isSubmitting={methods.formState.isSubmitting}
          className={actionButtonsClassName}
          onDelete={onDelete}
          deleteButtonText={deleteButtonText}
          variant={variant}
        />
      </form>
    </FormProvider>
  );
};

export const RoutePropertiesForm = forwardRef(RoutePropertiesFormComponent);
